import { useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn, formatNumber } from '@/lib/utils';

export interface TabDefinition<T extends string> {
  value: T;
  label: string;
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  /** Yorliqdagi sanoq — `undefined` bo'lsa umuman chizilmaydi. */
  count?: number;
  /** Sanoq nolga teng bo'lmaganda diqqatni tortishi kerakmi (navbatlar uchun). */
  urgent?: boolean;
}

export interface TabsProps<T extends string> {
  tabs: Array<TabDefinition<T>>;
  value: T;
  onChange: (value: T) => void;
  label: string;
  children: ReactNode;
}

interface Indicator {
  left: number;
  top: number;
  width: number;
  height: number;
}

const sameBox = (a: Indicator | null, b: Indicator): boolean =>
  a !== null &&
  a.left === b.left &&
  a.top === b.top &&
  a.width === b.width &&
  a.height === b.height;

/**
 * Tab paneli — sanoqlar bilan.
 *
 * Radix `Tabs` o'rniga qo'lda yozilgan, chunki bu yerdagi holat komponentniki EMAS: faol tab
 * URL da yashaydi (`?tab=`), ya'ni uni ulashish va unga "orqaga" bilan qaytish mumkin. Radix
 * variantida bu holatni tashqaridan boshqarish uni ikki manba bilan sinxronlashtirishga
 * aylanardi.
 *
 * Sanoq yorliqning O'ZIDA turadi: tab ochilmasdan turib "u yerda ish bormi" degan savolga
 * javob berishi kerak — aks holda operator to'rttasini navbat bilan ochib chiqadi.
 *
 * Faol tabning foni — alohida, SURILADIGAN element.
 *
 * Har bir tugmaning o'z foni bo'lganida almashuv bir kadrda sodir bo'lardi: bittasi so'nadi,
 * ikkinchisi yonadi, va ko'z ular orasidagi bog'liqlikni ko'rmasdi. Bitta fon ikkinchi
 * tugmaga SURILGANDA esa harakatning o'zi "sen shu yerdan u yerga o'tding" deb aytadi —
 * ayniqsa moderatsiyadagi to'rtta tab bir xil ko'ringanda foydali. O'lchov `useLayoutEffect`
 * da olinadi, ya'ni birinchi kadrdayoq fon o'z joyida; o'lchab bo'lmagan holda (masalan
 * server tomonda) faol tugma eski usulda o'z fonini oladi va ekran baribir to'g'ri ko'rinadi.
 */
export function Tabs<T extends string>({ tabs, value, onChange, label, children }: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<Indicator | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const measure = () => {
      const active = list.querySelector<HTMLElement>('[data-active="true"]');
      if (!active) {
        setIndicator(null);
        return;
      }
      const box: Indicator = {
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      };
      // Bir xil o'lchov qayta yozilmaydi: aks holda har o'lchov yangi render tug'dirardi.
      setIndicator((previous) => (sameBox(previous, box) ? previous : box));
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // `tabs` ning O'ZI emas, uzunligi: ro'yxat har renderda qaytadan tuziladi (sanoqlar
    // so'rovdan keladi), ya'ni havola bo'yicha bog'lanish cheksiz o'lchashga olib kelardi.
  }, [value, tabs.length]);

  return (
    <div className="flex flex-col gap-4">
      <div
        ref={listRef}
        role="tablist"
        aria-label={label}
        className="bg-muted/50 relative flex w-full flex-wrap gap-1 rounded-lg border p-1"
      >
        {indicator ? (
          <span
            aria-hidden="true"
            // Fon `--background` dan, `--card` dan emas: qorong'i mavzuda kartochka rangi
            // yo'lakcha foni (`muted/50`) bilan deyarli bir xil yorug'likda va faol tab
            // umuman ko'rinmay qolardi. Chegara esa ikkala mavzuda ham chekkani beradi.
            className="bg-background border-border/70 pointer-events-none absolute top-0 left-0 rounded-md border shadow-sm transition-transform duration-(--dur-control) ease-(--ease-out-soft)"
            style={{
              width: indicator.width,
              height: indicator.height,
              transform: `translate3d(${indicator.left}px, ${indicator.top}px, 0)`,
            }}
          />
        ) : null}

        {tabs.map((tab) => {
          const active = tab.value === value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              data-active={active}
              onClick={() => onChange(tab.value)}
              className={cn(
                'relative z-10 flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-(--dur-control)',
                active
                  ? cn('text-foreground', indicator === null && 'bg-background border shadow-sm')
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.icon ? <tab.icon className="size-4 shrink-0" aria-hidden /> : null}
              {tab.label}
              {/*
                Sanoq TINCH turadi — miltillamaydi ham, nafas olmaydi ham. Diqqatni tortadigan
                qismi rang: harakat esa o'qilishi kerak bo'lgan raqamning o'zini
                xiralashtirardi, holbuki operator aynan uni o'qiyapti.
              */}
              {tab.count === undefined ? null : (
                <Badge
                  variant={tab.urgent && tab.count > 0 ? 'warning' : 'secondary'}
                  className="tabular"
                >
                  {formatNumber(tab.count)}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/*
        Faqat FAOL tabning mazmuni chiziladi (`children` ni chaqiruvchi tanlaydi). Hammasini
        chizib, ko'rinmaganini yashirish to'rtta so'rovni bir vaqtda yuborardi — panel ochilishi
        bilan.

        `key` — faol tabning qiymati: tab almashganda mazmun qayta chiziladi va yumshoq
        paydo bo'ladi, ya'ni jadval bir zumda boshqa qatorlar bilan almashib qolmaydi.
      */}
      <div role="tabpanel" key={value} className="motion-fade">
        {children}
      </div>
    </div>
  );
}
